import { possessive } from '@shared';
import { Badge } from '@design-system';
import { OnboardingLayout, useSlideSetup, useOverviewSlide } from '@/features/client-onboarding';
import { OnboardingOverview } from '@/pages/OnboardingOverview';
import { OnboardingResultsAreIn } from '@/pages/OnboardingResultsAreIn';

import { ClipboardList, SlidersHorizontal, ShieldCheck } from 'lucide-react';

import { motion } from 'framer-motion';

interface Step {
  number: number;
  icon: React.ReactNode;
  title: string;
  description: string;
  isOptional?: boolean;
}

const GENWORTH_STEPS: Step[] = [
  {
    number: 1,
    icon: <ClipboardList className="text-muted-foreground size-6" />,
    title: 'Review Long-Term Care Stats',
    description:
      'See long-term care statistics from people similar to you, personalized using over 500 million datapoints.',
  },
  {
    number: 2,
    icon: <SlidersHorizontal className="text-muted-foreground size-6" />,
    title: 'Personalize Your Plan',
    description:
      'Refine your plan with your place of care and anticipated family caregiving support at each phase.',
  },
  {
    number: 3,
    icon: <ShieldCheck className="text-muted-foreground size-6" />,
    title: 'Evaluate Your Policy Options',
    description:
      'Explore how your policy options, like a reduced benefit or rate action, fit into your long-term care plan.',
  },
];

export function GenworthOnboardingOverview(_props: ClientIdProps) {
  // Reuse the canonical overview slide identity so the slide navigation, progress,
  // and titles are unaffected — only the rendered copy differs for Genworth.
  const { clientFirstName, visibleIndices, visibleLineIndices } = useOverviewSlide(
    GENWORTH_STEPS.length,
  );

  useSlideSetup({
    slidePage: OnboardingOverview,
    nextButtonLabel: "Let's Get Started",
    nextSlidePage: OnboardingResultsAreIn,
    hideProgressStepper: true,
  });

  return (
    <OnboardingLayout>
      <div className="flex flex-col items-center justify-center py-6 md:py-16">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mb-2 text-center"
        >
          <h1 className="text-balance text-2xl font-bold text-gray-950 md:text-4xl">
            Understanding {possessive(clientFirstName)}{' '}
            <span className="text-primary">Long-Term Care Policy</span>
          </h1>
        </motion.div>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="text-foreground text-balance text-center text-base md:text-lg"
        >
          Review your options and see how your policy fits into your long-term care plan
        </motion.p>
        <div className="relative mt-12 w-full max-w-2xl md:mt-16">
          {GENWORTH_STEPS.map((step, index) => {
            const isLastStep = index === GENWORTH_STEPS.length - 1;
            const isStepVisible = visibleIndices.includes(index);

            return (
              <div
                key={step.number}
                className="relative flex"
              >
                <div className="relative mr-4 flex flex-col items-center md:mr-6">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={isStepVisible ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
                    transition={{
                      duration: 0.2,
                      type: 'spring',
                      stiffness: 250,
                      damping: 15,
                    }}
                    className="relative"
                  >
                    <div className="border-border bg-background flex size-12 items-center justify-center rounded-xl border-2 md:size-14">
                      {step.icon}
                    </div>
                    <div className="bg-primary text-primary-foreground border-background absolute -right-2 -top-2 flex size-6 items-center justify-center rounded-full border-2 text-xs font-semibold">
                      {step.number}
                    </div>
                  </motion.div>

                  {!isLastStep && (
                    <motion.div
                      initial={{ scaleY: 0 }}
                      animate={visibleLineIndices.includes(index) ? { scaleY: 1 } : { scaleY: 0 }}
                      transition={{ duration: 0.1, ease: 'easeOut' }}
                      className="bg-border min-h-20 w-0.5 flex-1 origin-top md:min-h-16"
                    />
                  )}
                </div>

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={isStepVisible ? { opacity: 1, x: 0 } : { opacity: 0 }}
                  transition={{ duration: 0.3, delay: 0.15 }}
                  className="flex-1"
                >
                  <div className="flex items-center gap-2">
                    <h3 className="text-base font-semibold text-gray-950 md:text-lg">
                      {step.title}
                    </h3>
                    {step.isOptional && <Badge variant="secondary">Optional</Badge>}
                  </div>
                  <p className="text-foreground mt-1 text-sm leading-relaxed md:text-base">
                    {step.description}
                  </p>
                </motion.div>
              </div>
            );
          })}
        </div>
      </div>
    </OnboardingLayout>
  );
}
